import { DialogComponent } from '@theme/dialog';
import { CartLinesUpdateEvent } from '@shopify/events';

/**
 * Escape a string for safe insertion into HTML (text or attribute values).
 * @param {unknown} value
 * @returns {string}
 */
function escapeHtml(value) {
  return String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

/**
 * "Get the look" drawer. Extends Horizon's DialogComponent so it slides in from the right
 * at the same size/position as the cart drawer. Its product list is built client-side from
 * the JSON each hotspot in the same look publishes (nested-block product settings don't
 * resolve from the parent card). Adds route through /cart/add.js and dispatches a CartLinesUpdateEvent
 * so the cart drawer + bubble stay in sync.
 *
 * @extends {DialogComponent}
 */
class GetTheLookDrawer extends DialogComponent {
  #built = false;

  connectedCallback() {
    super.connectedCallback();
    this.#build();
    this.addEventListener('change', this.#handleVariantChange);
    this.addEventListener('click', this.#handleItemAdd);
  }

  /** @returns {{ add?: string, added?: string, sold_out?: string }} */
  get #i18n() {
    const el = document.querySelector(`script.gtl-i18n[data-for="${this.id}"]`);
    try {
      return el?.textContent ? JSON.parse(el.textContent) : {};
    } catch {
      return {};
    }
  }

  /** Build the drawer item list from this look's hotspot product payloads. */
  #build() {
    if (this.#built) return;

    const card = this.closest('.stl-card');
    const itemsContainer = this.querySelector('[data-gtl-items]');
    if (!card || !itemsContainer) return;

    const products = [];
    const seen = new Set();

    card.querySelectorAll('script.stl-hotspot-data').forEach((script) => {
      let product;
      try {
        product = JSON.parse(script.textContent || '');
      } catch {
        return;
      }
      if (!product?.id || seen.has(product.id)) return;
      seen.add(product.id);
      products.push(product);
    });

    const i18n = this.#i18n;
    const fragment = document.createDocumentFragment();
    products.forEach((product) => fragment.appendChild(this.#renderItem(product, i18n)));
    itemsContainer.replaceChildren(fragment);

    // Reveal the card's CTA only when the look actually has shoppable products.
    const cta = card.querySelector('.stl-card__cta');
    if (cta instanceof HTMLElement) cta.hidden = products.length === 0;

    this.#built = true;
  }

  /**
   * @param {any} product
   * @param {{ add?: string, added?: string, sold_out?: string }} i18n
   * @returns {HTMLElement}
   */
  #renderItem(product, i18n) {
    const variants = Array.isArray(product.variants) ? product.variants : [];
    const defaultVariantId = String(product.selected_variant_id || variants[0]?.id || '');

    const item = document.createElement('div');
    item.className = 'gtl-item';
    item.dataset.productId = String(product.id);
    item.dataset.available = String(Boolean(product.available));
    item.dataset.variantId = defaultVariantId;

    const selected = variants.find((v) => String(v.id) === defaultVariantId) || variants[0] || {};
    const url = escapeHtml(product.url);

    const media = product.image
      ? `<a class="gtl-item__media" href="${url}" tabindex="-1" aria-hidden="true"><img class="gtl-item__image" src="${escapeHtml(
          product.image
        )}" alt="" loading="lazy" width="84"></a>`
      : `<a class="gtl-item__media" href="${url}" tabindex="-1" aria-hidden="true"></a>`;

    let variantControl = '';
    if (!product.has_only_default_variant && variants.length > 1) {
      const options = variants
        .map((v) => {
          const label = v.available ? v.title : `${v.title} – ${i18n.sold_out || 'Sold out'}`;
          return `<option value="${escapeHtml(v.id)}" data-price="${escapeHtml(v.price)}"${
            v.available ? '' : ' disabled'
          }${String(v.id) === defaultVariantId ? ' selected' : ''}>${escapeHtml(label)}</option>`;
        })
        .join('');
      variantControl = `<select class="gtl-item__select" data-variant-select aria-label="${escapeHtml(
        product.title
      )}">${options}</select>`;
    }

    const addLabel = product.available ? i18n.add || 'Add to cart' : i18n.sold_out || 'Sold out';

    item.innerHTML = `
      ${media}
      <div class="gtl-item__info">
        <a class="gtl-item__title" href="${url}">${escapeHtml(product.title)}</a>
        <span class="gtl-item__price" data-price-display>${escapeHtml(selected.price || '')}</span>
        ${variantControl}
        <button class="gtl-item__add button button-secondary" type="button" data-add-one${
          product.available ? '' : ' disabled'
        }>${escapeHtml(addLabel)}</button>
      </div>`;

    return item;
  }

  /** Opens the drawer (called via on:click="/open"). */
  open() {
    this.showDialog();
  }

  /** Closes the drawer (called via on:click="/close"). */
  close() {
    this.closeDialog();
  }

  /**
   * Keep each item's selected variant id + displayed price in sync with its <select>.
   * @param {Event} event
   */
  #handleVariantChange = (event) => {
    const select = /** @type {HTMLSelectElement | null} */ (
      event.target instanceof Element ? event.target.closest('[data-variant-select]') : null
    );
    if (!select) return;

    const item = select.closest('.gtl-item');
    if (!(item instanceof HTMLElement)) return;

    const option = select.options[select.selectedIndex];
    item.dataset.variantId = select.value;

    const priceEl = item.querySelector('[data-price-display]');
    if (priceEl && option?.dataset.price) priceEl.textContent = option.dataset.price;
  };

  /**
   * Handle a single-item "Add" click.
   * @param {Event} event
   */
  #handleItemAdd = (event) => {
    const button = event.target instanceof Element ? event.target.closest('[data-add-one]') : null;
    if (!(button instanceof HTMLButtonElement)) return;

    const item = button.closest('.gtl-item');
    const variantId = item instanceof HTMLElement ? item.dataset.variantId : null;
    if (!variantId) return;

    this.#addToCart([{ id: variantId, quantity: 1 }], button);
  };

  /**
   * Add every available item in the look to the cart (called via on:click="/addAll").
   * @param {Event} event
   */
  addAll = (event) => {
    const button = /** @type {HTMLButtonElement | null} */ (
      event?.target instanceof Element ? event.target.closest('.gtl-drawer__add-all') : null
    );

    const items = Array.from(this.querySelectorAll('.gtl-item'))
      .filter((item) => item instanceof HTMLElement && item.dataset.available !== 'false' && item.dataset.variantId)
      .map((item) => ({ id: /** @type {HTMLElement} */ (item).dataset.variantId, quantity: 1 }));

    if (!items.length) return;

    this.#addToCart(items, button, { closeOnSuccess: true });
  };

  /**
   * POST to the cart and notify the theme so the cart drawer/bubble refresh.
   * @param {Array<{id: string|undefined, quantity: number}>} items
   * @param {HTMLButtonElement | null} button
   * @param {{ closeOnSuccess?: boolean }} [options]
   */
  #addToCart = async (items, button, options = {}) => {
    const originalText = button?.textContent;
    if (button) button.disabled = true;

    const totalQuantity = items.reduce((sum, item) => sum + (item.quantity || 1), 0);
    // 4.x cart contract: dispatch CartLinesUpdateEvent with a deferred promise; the
    // cart drawer/bubble listen for it and resolve their UI from the promise payload.
    const deferredEventPromise = CartLinesUpdateEvent.createPromise();
    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'get-the-look',
        lines: items
          .filter((item) => item.id)
          .map((item) => ({ merchandiseId: String(item.id), quantity: item.quantity || 1 })),
        promise: deferredEventPromise.promise,
      })
    );

    try {
      const response = await fetch(`${Theme.routes.cart_add_url}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items }),
      });

      const data = await response.json();

      if (!response.ok || data?.status) {
        deferredEventPromise.reject(new Error(data?.description || data?.message || 'Add to cart failed'));
        if (button) button.textContent = data?.description || data?.message || 'Unavailable';
        return;
      }

      // Fetch the updated cart so listeners can morph the drawer/bubble.
      const cart = await (
        await fetch(`${Theme.routes.cart_url}.json`, { headers: { Accept: 'application/json' } })
      ).json();
      deferredEventPromise.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          items: cart.items,
          source: 'get-the-look',
          sourceId: this.id?.toString?.() || 'get-the-look',
          itemCount: totalQuantity,
          didError: false,
        },
      });

      if (options.closeOnSuccess) {
        this.close();
      } else if (button) {
        const item = button.closest('.gtl-item');
        if (item instanceof HTMLElement) item.dataset.added = 'true';
        button.textContent = this.#i18n.added || 'Added';
      }
    } catch (error) {
      deferredEventPromise.reject(error);
      if (button && originalText != null) button.textContent = originalText;
      console.error('[get-the-look] add to cart failed', error);
    } finally {
      if (button) {
        window.setTimeout(() => {
          button.disabled = false;
          if (button.classList.contains('gtl-drawer__add-all') && originalText != null) {
            button.textContent = originalText;
          }
        }, 1200);
      }
    }
  };
}

if (!customElements.get('get-the-look-drawer')) {
  customElements.define('get-the-look-drawer', GetTheLookDrawer);
}

/**
 * Hotspot tap/click for the lean shop-the-look popups. The native product-hotspot-component
 * only opens its dialog on hover (desktop) or routes clicks to a quick-add modal (which our
 * lean popup doesn't have). This makes a click/tap toggle the popup on every breakpoint.
 */
document.addEventListener('click', (event) => {
  const trigger = event.target instanceof Element ? event.target.closest('.stl-hotspot .hotspot-trigger') : null;
  if (!trigger) return;

  const component = /** @type {any} */ (trigger.closest('product-hotspot-component'));
  if (!component) return;

  event.preventDefault();

  if (component.refs?.dialog?.open) {
    component.closeDialog?.();
  } else {
    component.showDialog?.();
  }
});

/**
 * Category tabs (Women / Men / …). Each shop-the-look section owns its own tabs: a Tab block
 * is a category, and the Looks inside it belong to it natively. Switching a tab shows that
 * tab's panel and hides the others within the same section, and reflects the choice in the URL
 * hash (e.g. #men) so a tab is directly linkable and survives refresh / back-forward.
 */

/**
 * Show one panel (and sync its section's tab bar). All other panels in the same section hide.
 * @param {HTMLElement} panel
 */
function stlShowPanel(panel) {
  const section = panel.closest('.shop-the-look');
  if (!section) return;
  const slug = panel.dataset.stlPanel;

  section.querySelectorAll('[data-stl-panel]').forEach((p) => {
    if (p instanceof HTMLElement) p.hidden = p !== panel;
  });
  section.querySelectorAll('[data-stl-tabbar] [data-stl-tab]').forEach((tab) => {
    if (!(tab instanceof HTMLElement)) return;
    const active = tab.dataset.stlTab === slug;
    tab.classList.toggle('is-active', active);
    tab.setAttribute('aria-selected', active ? 'true' : 'false');
    tab.tabIndex = active ? 0 : -1;
  });
}

/**
 * Activate a tab by slug within a section. Optionally writes the slug to the URL hash without
 * scrolling (the panels' ids are namespaced, so nothing on the page matches a bare slug).
 * @param {Element} section
 * @param {string} slug
 * @param {boolean} [updateHash]
 */
function stlActivate(section, slug, updateHash) {
  const panel = section.querySelector(`[data-stl-panel="${CSS.escape(slug)}"]`);
  if (!(panel instanceof HTMLElement)) return false;
  stlShowPanel(panel);
  if (updateHash) history.replaceState(null, '', `#${slug}`);
  return true;
}

/**
 * Build a tab bar's buttons from the section's panels. Nested theme-block settings (the labels)
 * aren't readable in Liquid at the section level, so the labels travel on each panel's
 * data-stl-label and the bar is assembled here.
 * @param {HTMLElement} tabbar
 * @param {HTMLElement[]} panels
 */
function stlBuildTabbar(tabbar, panels) {
  if (tabbar.dataset.stlBuilt === 'true') return;
  const fragment = document.createDocumentFragment();
  panels.forEach((panel) => {
    const slug = panel.dataset.stlPanel || '';
    if (!slug) return;
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'stl-tab';
    button.setAttribute('role', 'tab');
    button.dataset.stlTab = slug;
    if (panel.id) button.setAttribute('aria-controls', panel.id);
    button.textContent = panel.dataset.stlLabel || slug;
    fragment.appendChild(button);
  });
  tabbar.appendChild(fragment);
  tabbar.dataset.stlBuilt = 'true';
}

/**
 * Apply each tab's "row layout" pattern (e.g. "3, 2, 4") to its cards for an editorial,
 * uneven-row look. Each card gets a --card-cols-desktop equal to how many looks share its row,
 * which the stylesheet turns into a width. The pattern repeats if there are more looks than it
 * covers; blank means an even grid. Mobile ignores the pattern (handled in CSS).
 * @param {Element} section
 */
function stlApplyRowLayout(section) {
  section.querySelectorAll('.shop-the-look__grid').forEach((grid) => {
    if (!(grid instanceof HTMLElement)) return;
    const cards = Array.from(grid.children).filter(
      (c) => c instanceof HTMLElement && c.classList.contains('stl-card')
    );
    const pattern = String(grid.dataset.rowPattern || '')
      .split(/[^0-9]+/)
      .map((n) => parseInt(n, 10))
      .filter((n) => n >= 1);

    if (!pattern.length) {
      cards.forEach((c) => c.style.removeProperty('--card-cols-desktop'));
      return;
    }

    let i = 0;
    let row = 0;
    while (i < cards.length) {
      const size = pattern[row % pattern.length];
      for (let k = 0; k < size && i < cards.length; k += 1, i += 1) {
        cards[i].style.setProperty('--card-cols-desktop', String(size));
      }
      row += 1;
    }
  });
}

/** @param {Element} section */
function stlInitSection(section) {
  stlApplyRowLayout(section);

  const panels = /** @type {HTMLElement[]} */ (
    Array.from(section.querySelectorAll('[data-stl-panel]')).filter((p) => p instanceof HTMLElement)
  );
  if (!panels.length) return;

  const tabbar = section.querySelector('[data-stl-tabbar]');
  if (tabbar instanceof HTMLElement && panels.length > 1) stlBuildTabbar(tabbar, panels);

  const tabs = tabbar ? Array.from(tabbar.querySelectorAll('[data-stl-tab]')) : [];
  tabs.forEach((tab) => {
    if (!(tab instanceof HTMLElement) || tab.dataset.stlReady === 'true') return;
    tab.addEventListener('click', () => {
      if (tab.dataset.stlTab) stlActivate(section, tab.dataset.stlTab, true);
    });
    tab.dataset.stlReady = 'true';
  });

  // Initial tab: the URL hash if it matches a panel in this section, else the first panel.
  const hash = decodeURIComponent((location.hash || '').replace(/^#/, ''));
  if (!(hash && stlActivate(section, hash, false))) {
    const firstSlug = panels[0].dataset.stlPanel;
    if (firstSlug) stlActivate(section, firstSlug, false);
  }
}

/**
 * PTC: a section with data-stl-source shows another page's looks (single source of truth: the
 * homepage reads /pages/shop-the-look). Fetched once, when the section nears the viewport; its own
 * looks are swapped for the first N looks of each remote tab. On any failure the local looks stay.
 * @param {HTMLElement} section
 */
function stlLoadSource(section) {
  const src = section.dataset.stlSource;
  if (!src || section.dataset.stlSourced) return;
  section.dataset.stlSourced = 'pending';
  const limit = parseInt(section.dataset.stlLimit || '3', 10) || 3;

  const load = async () => {
    try {
      const sep = src.includes('?') ? '&' : '?';
      let html = '';
      const bySection = await fetch(`${src}${sep}section_id=shop_the_look_main`);
      if (bySection.ok) html = await bySection.text();
      let remote = html ? new DOMParser().parseFromString(html, 'text/html').querySelector('.shop-the-look') : null;
      if (!remote) {
        const full = await fetch(src);
        if (!full.ok) throw new Error(`HTTP ${full.status}`);
        remote = new DOMParser().parseFromString(await full.text(), 'text/html').querySelector('.shop-the-look');
      }
      const panels = remote ? Array.from(remote.querySelectorAll('[data-stl-panel]')) : [];
      if (!panels.length) throw new Error('no looks found');

      panels.forEach((panel) => {
        panel.querySelectorAll('.shop-the-look__grid').forEach((grid) => {
          Array.from(grid.children)
            .filter((c) => c.classList.contains('stl-card'))
            .slice(limit)
            .forEach((c) => c.remove());
        });
      });

      // Swap: drop the local tab bar + panels, insert the remote ones where the panels were.
      const localPanels = Array.from(section.querySelectorAll('[data-stl-panel]'));
      const anchor = localPanels[0]?.closest('.shopify-block') || localPanels[0];
      const insertBefore = anchor || section.querySelector('.shop-the-look__viewall');
      const remoteTabbar = remote.querySelector('[data-stl-tabbar]');
      const localTabbar = section.querySelector('[data-stl-tabbar]');
      const remoteBlocks = panels.map((p) => p.closest('.shopify-block') || p);
      remoteBlocks.forEach((b) => section.insertBefore(document.importNode(b, true), insertBefore || null));
      localPanels.forEach((p) => (p.closest('.shopify-block') || p).remove());
      if (localTabbar) localTabbar.remove();
      if (remoteTabbar) {
        const first = section.querySelector('[data-stl-panel]');
        const firstBlock = first?.closest('.shopify-block') || first;
        section.insertBefore(document.importNode(remoteTabbar, true), firstBlock);
      }
      section.dataset.stlSourced = 'true';
      stlInitSection(section);
    } catch (error) {
      section.dataset.stlSourced = 'failed';
      console.warn('[shop-the-look] could not load looks from', src, error);
    }
  };

  if (!('IntersectionObserver' in window)) return void load();
  const io = new IntersectionObserver(
    (entries) => {
      if (entries.some((e) => e.isIntersecting)) {
        io.disconnect();
        load();
      }
    },
    { rootMargin: '800px 0px' }
  );
  io.observe(section);
}

function stlInit() {
  document.querySelectorAll('.shop-the-look').forEach((section) => {
    stlInitSection(section);
    if (section instanceof HTMLElement && section.dataset.stlSource) stlLoadSource(section);
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', stlInit);
} else {
  stlInit();
}

// Back/forward or an external #hash link: re-activate the matching tab in every section.
window.addEventListener('hashchange', () => {
  const slug = decodeURIComponent((location.hash || '').replace(/^#/, ''));
  if (!slug) return;
  document.querySelectorAll('.shop-the-look').forEach((section) => stlActivate(section, slug, false));
});

// Theme editor: re-init on section load, and open a look's tab when its block is selected.
document.addEventListener('shopify:section:load', stlInit);
document.addEventListener('shopify:block:select', (event) => {
  const target = event.target;
  if (!(target instanceof Element)) return;
  const panel = target.closest('[data-stl-panel]') || target.querySelector?.('[data-stl-panel]');
  if (panel instanceof HTMLElement) stlShowPanel(panel);
});
